/**
 * jails/darwin — Seatbelt (sandbox-exec) backend for osJail (HARNESS-10 t28).
 *
 * Strategy: generate a conservative SBPL profile from the JailSpec and run
 * `sandbox-exec -p <profile> <program> <argv...>` as an ARGV PREFIX — the
 * child never sees a shell, so the profile path is the only injection
 * surface and it is generated by us.
 *
 * Honesty: if `/usr/bin/sandbox-exec` is missing (or the host is not darwin)
 * the probe reports UNAVAILABLE ⇒ with ZELARI_OS_JAIL=required the tool is
 * DENIED rather than run unjailed. The profile is best-effort containment
 * (Apple never fully documented SBPL); it is still a REAL kernel-level jail,
 * not a prompt.
 */
import { existsSync } from 'node:fs';
import type { JailBackend, JailProbeResult, JailSpec } from '../osJail.js';

/** Standard location of the Seatbelt launcher on macOS. */
export const SANDBOX_EXEC_PATH = '/usr/bin/sandbox-exec';

/** Quote a path for the SBPL profile (backslashes + quotes escaped). */
function sbplQuote(p: string): string {
  return `"${p.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

/**
 * Pure: generate the Seatbelt profile. Default posture: everything allowed
 * EXCEPT writes outside the JailSpec writable roots and network per the
 * spec (`deny` ⇒ deny network*; `allow`/`allow-list` ⇒ allowed — SBPL host
 * filtering is not portable, so allow-list degrades to allow and the
 * limitation is written INTO the profile, never hidden).
 */
export function buildSeatbeltProfile(spec: JailSpec): string {
  const lines: string[] = [
    '(version 1)',
    `; zelari osJail (t28) — generated from JailSpec root=${spec.root}`,
    '(allow default)',
    '(deny file-write*)',
    '(allow file-write*',
  ];
  for (const dir of spec.writable) {
    lines.push(`  (subpath ${sbplQuote(dir)})`);
  }
  lines.push(')');
  switch (spec.network.mode) {
    case 'deny':
      lines.push('(deny network*)');
      break;
    case 'allow':
      lines.push('(allow network*)');
      break;
    case 'allow-list':
      // Documented limitation: SBCL cannot filter by hostname portably —
      // the allow-list degrades to full allow HERE (kernel view); the
      // claims-level verdict still gates the tool call itself.
      lines.push(
        `; allow-list hosts (${spec.network.hosts.join(', ')}) NOT kernel-filtered — degraded to allow`,
        '(allow network*)',
      );
      break;
  }
  return lines.join('\n');
}

/** Pure probe decision (injectable platform + exists so tests run anywhere). */
export function darwinProbe(platform: string, exists: (p: string) => boolean): JailProbeResult {
  if (platform !== 'darwin') {
    return {
      backend: 'seatbelt',
      available: false,
      reason: `platform ${platform} is not darwin`,
    };
  }
  if (!exists(SANDBOX_EXEC_PATH)) {
    return {
      backend: 'seatbelt',
      available: false,
      reason: `${SANDBOX_EXEC_PATH} not found`,
    };
  }
  return {
    backend: 'seatbelt',
    available: true,
    reason: `sandbox-exec available at ${SANDBOX_EXEC_PATH}`,
  };
}

export const darwinBackend: JailBackend = {
  id: 'seatbelt',
  probe: () => darwinProbe(process.platform, (p) => existsSync(p)),
  wrap: (spec, program, argv) => ({
    program: SANDBOX_EXEC_PATH,
    argv: ['-p', buildSeatbeltProfile(spec), program, ...argv],
  }),
};
